import React from "react";
import styled from "styled-components";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import {
	ADMIN_NAV_CATALOG,
	ADMIN_NAV_SECTION_IDS,
	ADMIN_NAV_SECTION_LABELS,
	AdminNavSectionId,
	AdminNavItem,
} from "../../../../config/adminNavigationCatalog";
import { useAdminPermissions } from "../../../hooks/useAdminPermissions";
import { useIsSuperAdmin } from "../../../hooks/useIsSuperAdmin";

// ─── Styled components ───────────────────────────────────────────────────────

const ContentWrap = styled.div`
	padding-bottom: 8px;
`;

const RoleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 16px;
	font-size: 14px;
	color: #374151;
`;

const RoleLabel = styled.span`
	font-weight: 600;
	color: #111827;
`;

const FullAccessBadge = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background-color: #eff6ff;
	border: 1px solid #bfdbfe;
	border-radius: 6px;
	padding: 6px 12px;
	font-size: 13px;
	font-weight: 600;
	color: #1d4ed8;
	margin-bottom: 16px;
`;

const SectionBlock = styled.div`
	margin-bottom: 20px;
`;

const SectionTitle = styled.div`
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #6b7280;
	margin-bottom: 8px;
	padding-bottom: 4px;
	border-bottom: 1px solid #e5e7eb;
`;

const ToolRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 6px 0;
	font-size: 13px;
	color: #374151;
`;

const ToolName = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const ToolIcon = styled.i`
	width: 16px;
	font-size: 13px;
	text-align: center;
	color: #9ca3af;
	flex-shrink: 0;
`;

const AccessLevelBadge = styled.span<{ full?: boolean }>`
	font-size: 11px;
	font-weight: 500;
	padding: 2px 8px;
	border-radius: 4px;
	background-color: ${({ full }) => (full ? "#d1fae5" : "#f3f4f6")};
	color: ${({ full }) => (full ? "#065f46" : "#6b7280")};
	border: 1px solid ${({ full }) => (full ? "#a7f3d0" : "#e5e7eb")};
	white-space: nowrap;
`;

const EmptyNote = styled.div`
	font-size: 13px;
	color: #9ca3af;
	font-style: italic;
	padding: 4px 0;
`;

const Footer = styled.div`
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid #e5e7eb;
	font-size: 12px;
	color: #9ca3af;
`;

const FooterLink = styled.a`
	color: #3b82f6;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Determine access level for a tool item given the user's permission list.
 *
 * Logic:
 * - A tool's `permission` key is the primary view key (e.g. "orders.view").
 * - "Full" means the user also holds at least one action key that shares
 *   the same domain prefix but ends in something other than ".view"
 *   (e.g. "orders.edit", "orders.cancel", "partners.manage").
 * - "View only" means user has the view key but no such action keys.
 * - null means user has neither (should not appear after filtering).
 */
function computeAccessLevel(item: AdminNavItem, permissions: string[]): "full" | "view-only" {
	const viewKey = item.permission!;
	// Extract domain prefix: "orders.view" → "orders", "finance.debt.view" → "finance"
	const domainPrefix = viewKey.split(".")[0];
	const hasAction = permissions.some(
		(p) => p.startsWith(domainPrefix + ".") && !p.endsWith(".view") && p !== viewKey
	);
	return hasAction ? "full" : "view-only";
}

// ─── Component ───────────────────────────────────────────────────────────────

interface MyAccessDialogProps {
	open: boolean;
	onClose: () => void;
}

export default function MyAccessDialog({ open, onClose }: MyAccessDialogProps) {
	const { permissions, roleId, roleName, loaded, _allPermissions } = useAdminPermissions();
	const { isSuperAdmin } = useIsSuperAdmin();

	const roleDisplay = roleName ?? roleId ?? "—";

	// Build grouped items: for all-permissions show full catalog; otherwise filter.
	const groupedSections: { sectionId: AdminNavSectionId; label: string; items: AdminNavItem[] }[] =
		ADMIN_NAV_SECTION_IDS.map((sectionId) => {
			const sectionItems = ADMIN_NAV_CATALOG.filter((item) => {
				if (item.to === "/") return false; // skip "All tools" self-link
				if (item.hidden) return false; // gate-only detail pages — not standalone tools
				if (item.superAdminOnly && !isSuperAdmin) return false;
				if (item.section !== sectionId) return false;
				if (!item.permission) return false; // no-permission items are always visible but not role-specific
				if (_allPermissions) return true;
				return Array.isArray(permissions) && permissions.includes(item.permission);
			});
			return { sectionId, label: ADMIN_NAV_SECTION_LABELS[sectionId], items: sectionItems };
		}).filter((g) => g.items.length > 0);

	const handleClose = (_e: any, _reason: any) => onClose();

	const footerActions = (
		<button
			onClick={onClose}
			style={{
				padding: "8px 20px",
				border: "1px solid #e5e7eb",
				borderRadius: "8px",
				background: "#fff",
				cursor: "pointer",
				fontSize: "14px",
				color: "#374151",
			}}>
			Close
		</button>
	);

	return (
		<DialogWrapMedium
			open={open}
			onClose={handleClose}
			title="My access"
			actions={footerActions}
			hideFooter={false}>
			<ContentWrap>
				{/* Role row */}
				<RoleRow>
					<span>Role:</span>
					<RoleLabel>{loaded ? roleDisplay : "—"}</RoleLabel>
				</RoleRow>

				{/* Full-access system role badge */}
				{_allPermissions && (
					<FullAccessBadge>
						<i className="fa fa-shield" />
						Full access (system role)
					</FullAccessBadge>
				)}

				{/* Tool list grouped by section */}
				{!loaded && <EmptyNote>Loading your access…</EmptyNote>}

				{loaded && groupedSections.length === 0 && (
					<EmptyNote>No tools granted yet. Contact HR to get a role assigned.</EmptyNote>
				)}

				{loaded &&
					groupedSections.map(({ sectionId, label, items }) => (
						<SectionBlock key={sectionId}>
							<SectionTitle>{label}</SectionTitle>
							{items.map((item) => {
								const level =
									_allPermissions || !Array.isArray(permissions)
										? "full"
										: computeAccessLevel(item, permissions);
								return (
									<ToolRow key={item.to}>
										<ToolName>
											<ToolIcon className={item.icon} />
											{item.label}
										</ToolName>
										<AccessLevelBadge full={level === "full"}>
											{level === "full" ? "Full" : "View only"}
										</AccessLevelBadge>
									</ToolRow>
								);
							})}
						</SectionBlock>
					))}

				{/* Footer */}
				<Footer>
					Access is managed in Workspace by HR.{" "}
					<FooterLink
						href="https://work.whalelo.net"
						target="_blank"
						rel="noopener noreferrer">
						work.whalelo.net
					</FooterLink>
				</Footer>
			</ContentWrap>
		</DialogWrapMedium>
	);
}
